<template>
  <!--
    DsInput — implementación Vue del input del DS (elements/input/input.css).
    Consume tokens, scoped (clase ds-input). Modo shell automático cuando hay
    leading/trailing/prefix/suffix. v-model estándar. Ver docs/component-architecture.md.
  -->
  <div
    v-if="hasExtras"
    class="ds-input-shell"
    :class="{ 'is-error': error, 'ds-input-shell--wide': wide }"
  >
    <span v-if="$slots.leading" class="ds-input__icon"><slot name="leading" /></span>
    <span v-if="$slots.prefix" class="ds-input__affix"><slot name="prefix" /></span>
    <input
      ref="input"
      class="ds-input"
      :type="type"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :aria-invalid="error ? 'true' : undefined"
      v-bind="$attrs"
      @input="$emit('update:modelValue', $event.target.value)"
    />
    <span v-if="$slots.suffix" class="ds-input__affix"><slot name="suffix" /></span>
    <span v-if="$slots.trailing" class="ds-input__icon"><slot name="trailing" /></span>
  </div>

  <input
    v-else
    ref="input"
    class="ds-input"
    :class="{ 'is-error': error, 'ds-input--wide': wide }"
    :type="type"
    :value="modelValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :aria-invalid="error ? 'true' : undefined"
    v-bind="$attrs"
    @input="$emit('update:modelValue', $event.target.value)"
  />
</template>

<script>
export default {
  name: 'DsInput',
  inheritAttrs: false,
  props: {
    modelValue: { type: [String, Number], default: '' },
    type: { type: String, default: 'text' },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    /** fill container — default en plataforma (data-entry). */
    wide: { type: Boolean, default: true },
  },
  emits: ['update:modelValue'],
  computed: {
    hasExtras() {
      const s = this.$slots;
      return !!(s.leading || s.trailing || s.prefix || s.suffix);
    },
  },
};
</script>

<style scoped lang="scss">
.ds-input {
  font-family: var(--font-sans);
  font-size: var(--text-body-s-size);
  font-weight: 400;
  color: var(--text-default);
  background: var(--surface-elevated);
  height: 40px;
  padding: 0 var(--space-4);
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  outline: none;
  box-sizing: border-box;
  transition:
    border-color var(--duration-base, 240ms) var(--ease-default, ease),
    background var(--duration-base, 240ms) var(--ease-default, ease),
    box-shadow var(--duration-base, 240ms) var(--ease-default, ease);
}
.ds-input--wide { width: 100%; }

.ds-input::placeholder { color: var(--text-muted); opacity: 1; }

/* iOS zoom-on-focus guard: subir a 16px en touch. */
@media (pointer: coarse) {
  .ds-input, .ds-input__affix { font-size: 16px; }
}

/* Number/search resets */
.ds-input[type="number"]::-webkit-outer-spin-button,
.ds-input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.ds-input[type="number"] { -moz-appearance: textfield; }
.ds-input[type="search"]::-webkit-search-cancel-button { -webkit-appearance: none; }

/* Autofill — neutralizar el bg del browser con los tokens del control. */
.ds-input:-webkit-autofill,
.ds-input:-webkit-autofill:hover,
.ds-input:-webkit-autofill:focus {
  -webkit-text-fill-color: var(--text-default);
  -webkit-box-shadow: 0 0 0 1000px var(--surface-elevated) inset;
  caret-color: var(--text-default);
  transition: background-color 9999s ease-out 0s;
}

/* ── Shell (con extras) ── */
.ds-input-shell {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  height: 40px;
  padding: 0 var(--space-4);
  background: var(--surface-elevated);
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  box-sizing: border-box;
  transition:
    border-color var(--duration-base, 240ms) var(--ease-default, ease),
    box-shadow var(--duration-base, 240ms) var(--ease-default, ease);
}
.ds-input-shell--wide { display: flex; width: 100%; }
.ds-input-shell .ds-input {
  border: none;
  background: transparent;
  padding: 0;
  height: 100%;
  flex: 1;
  min-width: 0;
}
.ds-input-shell .ds-input:focus-visible { outline: none; box-shadow: none; }

.ds-input__affix {
  font-family: var(--font-sans);
  font-size: var(--text-body-s-size);
  font-weight: 400;
  color: var(--text-muted);
  user-select: none;
  white-space: nowrap;
  flex: 0 0 auto;
}
.ds-input__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  flex: 0 0 auto;
  :deep(svg) { width: 20px; height: 20px; }
}

/* ── Hover ── */
@media (hover: hover) and (pointer: fine) {
  .ds-input:hover:not([disabled]):not([readonly]),
  .ds-input-shell:hover { border-color: var(--text-muted); }
}

/* ── Focus — single accent band. ── */
.ds-input:focus-visible {
  border-color: var(--accent);
  box-shadow: 0 0 0 1px var(--accent);
  outline: none;
}
.ds-input-shell:focus-within {
  border-color: var(--accent);
  box-shadow: 0 0 0 1px var(--accent);
}

/* ── Error ── */
.ds-input.is-error,
.ds-input[aria-invalid="true"],
.ds-input-shell.is-error { border-color: var(--border-error); }
.ds-input.is-error:focus-visible,
.ds-input[aria-invalid="true"]:focus-visible,
.ds-input-shell.is-error:focus-within {
  border-color: var(--border-error);
  box-shadow: 0 0 0 1px var(--border-error);
}

/* ── Disabled ── */
.ds-input[disabled],
.ds-input-shell:has(.ds-input[disabled]) {
  background: var(--surface-disabled);
  color: var(--text-disabled);
  border-color: var(--border-disabled);
  cursor: not-allowed;
  pointer-events: none;
}
.ds-input[disabled]::placeholder { color: var(--text-disabled); }

/* ── Readonly — atenuado pero legible/focusable. ── */
.ds-input[readonly],
.ds-input-shell:has(.ds-input[readonly]) {
  background: var(--surface-disabled);
  border-color: var(--border-control);
  cursor: default;
}
.ds-input[readonly]:focus-visible {
  border-color: var(--border-control);
  box-shadow: 0 0 0 1px var(--border-control);
}

/* ── Slate/immersive cascade — transparent + border vellum-ish. ── */
:deep([data-surface="immersive"]) .ds-input:not([disabled]):not([readonly]),
:deep([data-surface="chrome"]) .ds-input:not([disabled]):not([readonly]) {
  background: transparent;
  color: var(--vellum);
}
</style>
